const FILE_EXTENSION_PATTERN = /\.[^.]+$/

// Captures one frame of a local video file as a JPEG so an uploaded clip gets a
// poster without waiting for a transcode. The frame is taken shortly after the
// start (the first frame of a clip is often black) and downscaled to a phone-
// friendly width. Decoding happens in the browser, so codecs the browser cannot
// play (HEVC .mov on Chrome, for example) reject and the operator picks a poster
// by hand.
const POSTER_MAX_WIDTH = 1280
const POSTER_QUALITY = 0.86
const POSTER_TIMEOUT_MS = 20_000

export function posterFileName(videoName) {
  const base = String(videoName || 'video').replace(FILE_EXTENSION_PATTERN, '') || 'video'
  return `${base}-poster.jpg`
}

export function captureVideoPoster(file, { maxWidth = POSTER_MAX_WIDTH, quality = POSTER_QUALITY, timeoutMs = POSTER_TIMEOUT_MS } = {}) {
  return new Promise((resolve, reject) => {
    const objectURL = URL.createObjectURL(file)
    const video = document.createElement('video')
    let settled = false
    const finish = (callback, value) => {
      if (settled) return
      settled = true
      window.clearTimeout(timer)
      video.onloadedmetadata = null
      video.onseeked = null
      video.onerror = null
      video.removeAttribute('src')
      video.load()
      URL.revokeObjectURL(objectURL)
      callback(value)
    }
    const timer = window.setTimeout(() => finish(reject, new Error('Poster capture timed out')), timeoutMs)
    video.muted = true
    video.playsInline = true
    video.preload = 'auto'
    video.onerror = () => finish(reject, new Error('This video cannot be decoded in the browser'))
    video.onloadedmetadata = () => {
      const duration = Number.isFinite(video.duration) ? video.duration : 0
      video.currentTime = duration > 0 ? Math.min(1, duration / 10) : 0
    }
    video.onseeked = () => {
      const scale = Math.min(1, maxWidth / (video.videoWidth || maxWidth))
      const canvas = document.createElement('canvas')
      canvas.width = Math.round(video.videoWidth * scale)
      canvas.height = Math.round(video.videoHeight * scale)
      if (!canvas.width || !canvas.height) return finish(reject, new Error('The video has no picture to capture'))
      canvas.getContext('2d').drawImage(video, 0, 0, canvas.width, canvas.height)
      canvas.toBlob((blob) => {
        if (!blob) return finish(reject, new Error('The poster could not be encoded'))
        finish(resolve, new File([blob], posterFileName(file.name), { type: 'image/jpeg' }))
      }, 'image/jpeg', quality)
    }
    video.src = objectURL
  })
}
